/* eslint-disable class-methods-use-this */

import {isString, mapValues} from 'lodash';

import {FeatureFlagsEnum} from '../../../../store/featureFlags/featureFlagsTypes';
import {decorateWithClientId} from '../../commons/clientIdModel';
import {Parameter} from '../../commons/parameterModel';
import {buildDefaultAtomParams, ClientAtom, UninferredAtom} from '../expressionAtomModel';
import {ExpressionFeatureFlagsType} from '../expressionFeatureFlags';
import {buildEnumParameter} from '../expressionParamHelpers';
import type {ExpressionsContext} from '../expressionsContext';
import {ExpressionDependencies} from '../expressionState';
import {AtomParams, PreAtom} from '../iotsCodecs';
import {buildAtomDescription, buildAtoms, InferredType, ResultType, UnknownResult} from './internal';

/**
 * Each transform has a category that is used to organize them into sections.
 * This is interpreted to build the UI.
 */

export enum MethodCategoriesEnum {
  PROPERTY = 'property',
  CUSTOM_FIELD = 'custom_field',
  ITEM = 'item',
  TRANSFORM = 'transform',
  EXTRACT = 'extract',
  COMPARE = 'compare',
  PARSE = 'parse',
  CONVERT = 'convert',
  CREATE = 'create',
  SERVER = 'server',
}

interface MethodOptions {
  hasFunction?: boolean /** default = false */;
  featureFlag?: ExpressionFeatureFlagsType;
}

export abstract class Method {
  /** Section in which the method will be grouped in the add atom dropdown. */
  category: MethodCategoriesEnum;
  /** Method has a sub-expression (like map, filter, sort_by). */
  hasFunction: boolean;
  /** Hide the method via feature flag. */
  featureFlag?: FeatureFlagsEnum;

  constructor(category: MethodCategoriesEnum, options: MethodOptions = {}) {
    const {hasFunction = false, featureFlag} = options;

    this.category = category;
    this.hasFunction = hasFunction;
    this.featureFlag = featureFlag;
  }

  /** Predict the type of the result that will be produced when evaluating real data. */
  abstract inferType(
    atom: UninferredAtom,
    context: ExpressionsContext,
    dependencies: ExpressionDependencies,
  ): InferredType;

  inferFunctionStartType(previousInfer: InferredType): InferredType {
    return previousInfer;
  }

  /** Additional parameters the method needs. Will be rendered in the atom card. */
  getParameters(): ReadonlyArray<Parameter> {
    return [];
  }

  /** Pre-populate parameters with default values. */
  getAtomDefaultParameters(_context?: ExpressionsContext): AtomParams | undefined {
    return undefined;
  }

  /** Used to build the default name of the expression, if the user has not overridden it. */
  getDefaultName(
    atom: ClientAtom,
    _context: ExpressionsContext,
    dependencies: ExpressionDependencies,
  ): string | undefined {
    return buildAtomDescription(atom).name;
  }

  /**
   * Predict the result that will be produced when evaluating in the rule engine.
   * @param preAtom Atom that the user has decided to add to the expression. It's not an atom yet because we don't know its type.
   * @param previousInfer Type of previous atom in the expression chain.
   * @param context Context with all previous expressions and environment like available custom fields.
   * @param dependencies Dependencies for atom infer that are outside the expressions' context.
   */
  infer(
    preAtom: PreAtom,
    previousInfer: InferredType,
    context: ExpressionsContext,
    dependencies: ExpressionDependencies,
  ): ClientAtom {
    let functionAtoms;

    // Build sub-function first, because the inferred atom might depend on it.
    if (this.hasFunction) {
      const functionStartType = this.inferFunctionStartType(previousInfer);
      functionAtoms = buildAtoms(preAtom.functionAtoms || [], functionStartType, context, dependencies);
    }

    const params =
      preAtom.params ??
      this.getAtomDefaultParameters(context) ??
      buildDefaultAtomParams(this.getParameters());

    const atom: UninferredAtom = {
      previousInfer,
      transform: preAtom.transform,
      params,
      functionAtoms,
      isValid: true,
    };

    const inferredType = this.inferType(atom, context, dependencies);

    return decorateWithClientId({
      ...atom,
      inferredType,
    });
  }
}

interface StaticallyTypedMethodOptions {
  featureFlag?: ExpressionFeatureFlagsType;
}

export class StaticallyTypedMethod extends Method {
  inferredType: InferredType;

  constructor(
    category: MethodCategoriesEnum,
    inferredType: InferredType,
    options: StaticallyTypedMethodOptions = {},
  ) {
    const {featureFlag} = options;
    super(category, {featureFlag});
    this.inferredType = inferredType;
  }

  inferType() {
    return this.inferredType;
  }
}

export class ParseMethod extends StaticallyTypedMethod {
  constructor(inferredType: InferredType) {
    super(MethodCategoriesEnum.PARSE, inferredType);
  }
}

export class ConvertMethod extends StaticallyTypedMethod {
  constructor(inferredType: InferredType) {
    super(MethodCategoriesEnum.CONVERT, inferredType);
  }
}

export class TransformMethod extends StaticallyTypedMethod {
  constructor(inferredType: InferredType) {
    super(MethodCategoriesEnum.TRANSFORM, inferredType);
  }
}

export class PropertyGetter extends Method {
  type: ResultType;

  constructor(type: ResultType) {
    super(MethodCategoriesEnum.PROPERTY);
    this.type = type;
  }

  inferType(atom: UninferredAtom): InferredType {
    const {property} = atom.params;
    if (!isString(property)) {
      return UnknownResult;
    }

    return (this.type.properties || {})[property] || UnknownResult;
  }

  getParameters(): ReadonlyArray<Parameter> {
    const propertyNames = mapValues(this.type.properties || {}, (_, key) => key);

    return [buildEnumParameter('get', propertyNames)];
  }
}
